import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { Button, makeStyles, Paper, Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import Loader from 'src/components/Loader';
import { BASE_URL } from '../../../../routes';
import Modal from '../../../../components/Modal';
import TableView from '../../../../components/TableView';
import DeleteDialog from '../../../../components/DeleteDialog';
import { Person } from '../../../../store/modules/person/types';
import LinkedAppointmentLicenses from './LinkedAppointmentLicenses';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import {
    AppointmentLicenses,
    ConfiguredFields,
    IColumn,
    License,
    Order,
} from '../../../../store/modules/application/types';
import { getFlatColumnFieldsFromList, getFormattedColumns } from 'src/utils/utils';

type LicenseProps = {
    licenses: any[];
    sortBy: string;
    sortOrder: Order;
    personList: Person[];
    appointmentId: string;
    appointmentLicensesLoading: boolean;
    personLicenses: License[];
    personLicensesLoadingStatus: boolean;
    setSortOrder: Function;
    setSortColumn: Function;
    addLicensesForAppointments: Function;
    getLinkedLicensesForAppointments: Function;
    getFilteredLicensesForAppointments: Function;
    removeLicenseFromAppointment: Function;
    appointmentLicenseColumnFields: ConfiguredFields;
};

const useStyles = makeStyles({
    addLicenses: {
        color: '#0277BD',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
    },
    addLicensesContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: 10,
    },
    countResults: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
    },
    metadataContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
});

const AppointmentLicense: FunctionComponent<LicenseProps> = (props) => {
    /* State for modals */
    const {
        licenses,
        sortBy,
        sortOrder,
        setSortOrder,
        appointmentId,
        setSortColumn,
        personLicenses,
        appointmentLicensesLoading,
        addLicensesForAppointments,
        removeLicenseFromAppointment,
        personLicensesLoadingStatus,
        getLinkedLicensesForAppointments,
        getFilteredLicensesForAppointments,
        appointmentLicenseColumnFields,
    } = props;

    const columns: IColumn[] = appointmentLicenseColumnFields?.fields?.length
        ? getFormattedColumns(appointmentLicenseColumnFields)
        : [
              {
                  id: 'licenseNumber|string',
                  label: 'License Number',
                  maxWidth: 300,
                  field: 'licenseNumber',
              },
              {
                  id: 'type|string',
                  label: 'License Type',
                  align: 'left',
                  maxWidth: 300,
                  field: 'type',
              },
              {
                  id: 'status|string',
                  label: 'Status',
                  align: 'left',
                  maxWidth: 300,
                  field: 'status',
              },
              {
                  id: 'issueDate|date',
                  label: 'Issue Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'issueDate',
              },
              {
                  id: 'expirationDate|date',
                  label: 'Expiration Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'expirationDate',
              },
          ];

    const [showLicensesModal, setShowLicensesModal] = useState<boolean>(false);
    const classes = useStyles();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [licenseId, setLicenseId] = useState<string>('');
    const history = useHistory();

    const routeChange = (path: string) => {
        history.push(path);
    };

    useEffect(() => {
        getLinkedLicensesForAppointments(appointmentId);
    }, [getLinkedLicensesForAppointments, appointmentId]);

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/license/${id}/detail`);
    };

    const handleAddLicensesModal = (shouldCloseModal: boolean) => {
        setShowLicensesModal(shouldCloseModal);
    };

    const handleAddLicensesForAppointments = (selectedLicenses: AppointmentLicenses) => {
        addLicensesForAppointments(selectedLicenses, appointmentId);
    };

    const removeAppointment = () => {
        setShowConfirmModal(false);
        removeLicenseFromAppointment(appointmentId, licenseId);
    };
    const handleRemove = (id: string, e: MouseEvent) => {
        setLicenseId(id);
        setShowConfirmModal(true);
        e.stopPropagation();
    };
    const onClose = () => {
        setShowConfirmModal(false);
    };
    if (appointmentLicensesLoading) return <Loader />;

    const appointmentLicenses = appointmentLicenseColumnFields?.fields?.length
        ? getFlatColumnFieldsFromList(licenses, columns)
        : licenses.map((license: any) => {
              return {
                  id: license?.id,
                  licenseNumber: license?.licenseNumber,
                  type: license?.type,
                  status: license?.status,
                  issueDate: license?.issueDate,
                  expirationDate: license?.expirationDate,
              };
          });

    return (
        <div className="verticalFillContainer">
            <Box className={classes.metadataContainer}>
                <span className={classes.countResults}>{`${licenses?.length} Results`}</span>
                <div className={classes.addLicensesContainer}>
                    <Button
                        startIcon={<AddIcon />}
                        className={classes.addLicenses}
                        onClick={(e: MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            handleAddLicensesModal(true);
                        }}
                    >
                        ADD LICENSES
                    </Button>
                </div>
            </Box>

            <Paper className="case-entries-container verticalFillContent fullHeight">
                {licenses.length > 0 ? (
                    <TableView
                        columns={columns}
                        data={appointmentLicenses}
                        count={licenses.length}
                        onClickRow={onClickRow}
                        deleteFlag={false}
                        getAppIcon={false}
                        editFlag={false}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                        removeFlag={true}
                        handleRemove={handleRemove}
                        removeTooltipLabel={'Remove from appointment'}
                    />
                ) : (
                    <TableEmptyState
                        title="No active record for this license"
                        message="All your linked licenses will be stored here"
                    ></TableEmptyState>
                )}
            </Paper>
            <Modal open={showLicensesModal} maxWidth={'sm'} fullWidth={true}>
                <LinkedAppointmentLicenses
                    appointmentLicenses={appointmentLicenses}
                    personLicenses={personLicenses}
                    handleAddLicensesModal={handleAddLicensesModal}
                    personLicensesLoadingStatus={personLicensesLoadingStatus}
                    addLicensesForAppointments={handleAddLicensesForAppointments}
                    getFilteredLicensesForAppointments={getFilteredLicensesForAppointments}
                />
            </Modal>
            {showConfirmModal && (
                <Modal open={true} maxWidth={'sm'} fullWidth={true} minHeight={'10vh'}>
                    <DeleteDialog
                        onClose={onClose}
                        onDelete={removeAppointment}
                        contentTitle="Are you sure you'd like to proceed with this action?"
                        content=""
                        buttonContent="Yes, Remove"
                    />
                </Modal>
            )}
        </div>
    );
};

export default AppointmentLicense;
